import { Container } from "components/core/container"
import { PageTitle } from "components/core/pageTitle"
import { SEO } from "components/core/seo"
import { ExploreItem } from "components/exploreItem"
import { GetStaticProps, NextPage } from "next"
import { selectPublicDashboards } from "repository/dashboard"
import { Dashboard, PublicDashboardUser } from "domainModels/dashboard"
import { BASE_URL } from "lib/utils"
import { supabaseAdmin } from "lib/supabase/admin"

interface Props {
  dashboards: Dashboard[]
  publicDashboardUsers: PublicDashboardUser[]
}

const ExplorePage: NextPage<Props> = ({ dashboards, publicDashboardUsers }) => (
  <div>
    <SEO
      title="Explore"
      description="You can explore the most interesting dashboards created on Frontend Toolkit here."
      ogImage={{
        title: "Explore",
        subTitle: "Discover interesting dashboards",
        url: `${BASE_URL}/explore`,
      }}
    />

    <Container>
      <PageTitle title="Explore" subtitle="Find interesting dashboards from other users" />

      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 mt-12">
        {dashboards?.map((dashboard) => (
          <ExploreItem
            key={dashboard.id}
            dashboard={dashboard}
            publicDashboardUser={publicDashboardUsers.find((pdu) => pdu.dashboardId === dashboard.id)}
          />
        ))}
      </div>
    </Container>
  </div>
)

export const getStaticProps: GetStaticProps<Props> = async () => {
  const dashboards = (await selectPublicDashboards(90)) ?? []
  const dashboardUserIds = dashboards.map((dashboard) => dashboard.user_id)

  const { data: users } = await supabaseAdmin.from("user").select().in("id", dashboardUserIds)
  const publicDashboardUsers: PublicDashboardUser[] = (dashboards ?? []).map((dashboard) => {
    const user = users?.find((user) => user.id === dashboard.user_id)
    return {
      dashboardId: dashboard.id,
      name: user?.full_name ?? "",
      avatar: user?.avatar_url ?? "",
      createdAt: user?.created_at ?? "",
      isPro: !!user?.stripe_customer_id,
    }
  })

  return {
    revalidate: 60,
    props: {
      // https://github.com/vercel/next.js/issues/11993
      dashboards: JSON.parse(JSON.stringify(dashboards)),
      publicDashboardUsers,
    },
  }
}

export default ExplorePage
